import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CombinedError, useClient, useQuery } from 'urql';

import { logger } from '@common';
import { retryWithBackoff } from '@frontend/app/utils/retry';
import { useAuth } from '@frontend/context/authContext';
import { GetSocialAnalyticsPostsWithInsightsQuery, PagingInput, PostInsights } from '@frontend/gql/social/graphql';

class PostsDataAbortError extends Error {
  constructor(message = 'Request was aborted') {
    super(message);
    this.name = 'PostsDataAbortError';
  }
}

import { Group, Project } from '../components/filters-form/types';
import { getSocialAnalyticsPosts, getSocialAnalyticsPostsWithInsights } from '../graphql';
import { QueryVariables } from '../types';

const PAGING_CONFIG = {
  limit: 200,
  concurrency: 10,
} as const;

// Fields that only affect presentation/display and don't require data refetch
const PRESENTATION_FIELDS = ['viewMode', 'postId'] as const;

interface UsePostsDataProps {
  clientId: string | undefined;
  tab: string | undefined;
  queryVariables: QueryVariables;
  projects: Project[];
  groups: Group[];
  onError?: (error: Error) => void;
}

export type EnrichedPost = PostInsights & {
  project: { id: number; name: string } | null;
  group: { id: number; name: string } | null;
};

export type EnrichedPostsData = Omit<GetSocialAnalyticsPostsWithInsightsQuery, 'client'> & {
  client?: Omit<NonNullable<GetSocialAnalyticsPostsWithInsightsQuery['client']>, 'socialAnalytics'> & {
    socialAnalytics?: Omit<
      NonNullable<NonNullable<GetSocialAnalyticsPostsWithInsightsQuery['client']>['socialAnalytics']>,
      'posts'
    > & {
      posts?: Omit<
        NonNullable<
          NonNullable<NonNullable<GetSocialAnalyticsPostsWithInsightsQuery['client']>['socialAnalytics']>['posts']
        >,
        'results'
      > & {
        results: EnrichedPost[];
      };
    };
  };
};

// Type for tracking local updates
export interface LocalUpdate {
  entityId: string;
  projectId?: string;
  projectName?: string;
  groupId?: string;
  groupName?: string;
  timestamp: number;
}

// Progressive loading function parameters
interface ProgressiveLoadingParams {
  totalCount: number;
  currentCount: number;
  bypassCache?: boolean;
}

type ProgressiveLoadingFunction = (params: ProgressiveLoadingParams) => Promise<void>;

export const usePostsData = ({
  clientId,
  tab,
  queryVariables,
  projects,
  groups,
  onError,
}: UsePostsDataProps): {
  data: EnrichedPostsData | undefined;
  error: CombinedError | undefined;
  fetchingPosts: boolean;
  fetchingInitialPosts: boolean;
  fetchingAdditionalPosts: boolean;
  fetchProgress: number;
  totalPosts: number;
  loadedPosts: number;
  handleRefresh: () => void;
  updateLocalAssignment: (update: Omit<LocalUpdate, 'timestamp'>) => void;
} => {
  // Store local updates with timestamps
  const [localUpdates, setLocalUpdates] = useState<Record<string, LocalUpdate>>({});

  // Track additional posts and loading progress
  const [additionalPosts, setAdditionalPosts] = useState<PostInsights[]>([]);
  const [fetchingAdditionalPosts, setFetchingAdditionalPosts] = useState(false);
  const [totalPosts, setTotalPosts] = useState(0);
  const [isRefreshMode, setIsRefreshMode] = useState(false);

  // Use ref to track if we're already fetching to prevent duplicate fetches
  const isFetchingRef = useRef(false);
  // Simple abort controller - only used when we need to cancel
  const abortControllerRef = useRef<AbortController | null>(null);
  // Generation counter to prevent stale requests from updating state
  const generationRef = useRef(0);

  const client = useClient();
  const { token } = useAuth();

  // Function to cancel any ongoing progressive requests
  const cancelOngoingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      logger.debug('[usePostsData] 🛑 Cancelling ongoing progressive loading requests', {
        isFetching: isFetchingRef.current,
        generation: generationRef.current,
        abortControllerExists: !!abortControllerRef.current,
      });
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Increment generation to invalidate any in-flight requests
    generationRef.current += 1;
  }, []); // No dependencies needed - uses refs for state

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Use ref to avoid dependency issues - we only want this to run on unmount
      if (abortControllerRef.current) {
        logger.debug('[usePostsData] 🛑 Component unmounting - cancelling ongoing requests');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      generationRef.current += 1;
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Function to reset all progressive loading state
  const resetProgressiveState = useCallback(() => {
    cancelOngoingRequests(); // Cancel first
    setAdditionalPosts([]);
    setFetchingAdditionalPosts(false);
    setTotalPosts(0);
    setIsRefreshMode(false);
    isFetchingRef.current = false;
  }, [cancelOngoingRequests]);

  // Remove presentation-only fields to detect data-affecting changes
  const omitPresentationFields = useCallback((variables: QueryVariables) => {
    const dataFields = { ...variables };
    PRESENTATION_FIELDS.forEach((field) => {
      delete dataFields[field];
    });
    return dataFields;
  }, []);

  // Reset progressive loading state when data-affecting filters change
  const queryVariablesRef = useRef(omitPresentationFields(queryVariables));
  useEffect(() => {
    const currentDataFields = omitPresentationFields(queryVariables);
    const hasDataFiltersChanged = JSON.stringify(currentDataFields) !== JSON.stringify(queryVariablesRef.current);

    if (hasDataFiltersChanged) {
      logger.debug('[usePostsData] 🔄 Data-affecting query variables changed, resetting progressive loading state', {
        previousFilters: Object.keys(queryVariablesRef.current || {}),
        newFilters: Object.keys(currentDataFields || {}),
        ignoredFields: PRESENTATION_FIELDS,
      });
      resetProgressiveState();
    }
    queryVariablesRef.current = currentDataFields;
  }, [queryVariables, resetProgressiveState, omitPresentationFields]);

  // Store the progressive loading function in a ref to avoid dependency issues
  const startProgressiveLoadingRef = useRef<ProgressiveLoadingFunction | null>(null);

  startProgressiveLoadingRef.current = async ({
    totalCount,
    currentCount,
    bypassCache = false,
  }: ProgressiveLoadingParams) => {
    const remainingCount = totalCount - currentCount;
    const remainingPages = Math.ceil(remainingCount / PAGING_CONFIG.limit);

    if (remainingPages <= 0) return;

    // Create abort controller for this progressive loading session
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Capture current generation - only this generation can update state
    const currentGeneration = generationRef.current;

    logger.debug('[usePostsData] 🚀 Starting additional pages fetch:', {
      totalCount,
      currentCount,
      remainingCount,
      remainingPages,
      generation: currentGeneration,
      bypassCache,
    });

    isFetchingRef.current = true;
    setFetchingAdditionalPosts(true);

    // Generate paging inputs for remaining pages
    const pagingInputs: PagingInput[] = Array.from({ length: remainingPages }, (_, i) => ({
      limit: PAGING_CONFIG.limit,
      offset: currentCount + i * PAGING_CONFIG.limit,
    }));

    try {
      await Promise.limitedAll(
        pagingInputs,
        async (paging) => {
          try {
            // Simple abort check - if aborted, skip this request
            if (abortController.signal.aborted) {
              return [];
            }

            const result = await retryWithBackoff(
              async () => {
                // Check abort status before each retry attempt
                if (abortController.signal.aborted) {
                  throw new PostsDataAbortError();
                }

                const queryResult = await client
                  .query(
                    getSocialAnalyticsPosts,
                    {
                      clientId: clientId || '',
                      startDate: queryVariables.startDate || '',
                      endDate: queryVariables.endDate || '',
                      postTypes: queryVariables.postTypes || [],
                      includeEstimates: queryVariables.includeEstimates || false,
                      postDateIntervalEnabled: queryVariables.postDateIntervalEnabled || false,
                      networks: queryVariables.networks || [],
                      projectIds: queryVariables.projectIds,
                      groupIds: queryVariables.groupIds,
                      memberIds: queryVariables.memberIds,
                      segments: queryVariables.segments,
                      mentionSources: queryVariables.mentionSources,
                      paging,
                    },
                    {
                      // Use network-only for refresh, cache-first for initial load/filter changes
                      requestPolicy: bypassCache ? 'network-only' : 'cache-first',
                      fetchOptions: {
                        signal: abortController.signal,
                        ...(token && { headers: { authorization: `Bearer ${token}` } }),
                      },
                    },
                  )
                  .toPromise();

                // Check if urql returned an error in the result
                if (queryResult.error) {
                  throw queryResult.error;
                }

                return queryResult;
              },
              {
                maxRetries: 3,
                initialDelay: 1000,
                maxDelay: 8000,
                backoffMultiplier: 2,
                shouldRetry: (error) => {
                  // Don't retry on abort errors
                  return !(error instanceof PostsDataAbortError);
                },
              },
            );

            const pageResults = result.data?.client?.socialAnalytics?.posts?.results || [];

            // Check both abort status AND generation to prevent stale data contamination
            const isStale = abortController.signal.aborted || generationRef.current !== currentGeneration;
            if (isStale) {
              return [];
            }

            if (pageResults.length > 0) {
              setAdditionalPosts((prev) => {
                // Double-check generation inside state setter to be extra safe
                if (generationRef.current !== currentGeneration) {
                  logger.debug('[usePostsData] 🚨 Race condition detected - ignoring state update', {
                    offset: paging.offset,
                    requestGeneration: currentGeneration,
                    currentGeneration: generationRef.current,
                  });
                  return prev; // Return unchanged state
                }

                // Deduplicate based on entityId to prevent duplicates
                const existingIds = new Set(prev.map((p) => p.entityId));
                const newPosts = pageResults.filter((p) => !existingIds.has(p.entityId));

                return [...prev, ...newPosts];
              });
            }

            return pageResults;
          } catch (error) {
            // Don't log aborted requests as errors
            if (error instanceof PostsDataAbortError) {
              return [];
            }

            // Log the error and report it to the UI
            logger.error('[usePostsData] Error in progressive loading request after retries:', {
              error,
              offset: paging.offset,
              limit: paging.limit,
              generation: currentGeneration,
            });

            // Report error to UI for toast notification
            onError?.(error instanceof Error ? error : new Error('Unknown error'));

            return [];
          }
        },
        PAGING_CONFIG.concurrency,
      );
    } catch (error) {
      // Don't log aborted operations as errors
      if (!(error instanceof PostsDataAbortError)) {
        logger.error('[usePostsData] Error in limitedAll:', error);
      }
    } finally {
      // Only clean up if this is still the current abort controller
      if (abortControllerRef.current === abortController) {
        isFetchingRef.current = false;
        setFetchingAdditionalPosts(false);
        abortControllerRef.current = null;
        logger.debug('[usePostsData] ✅ Progressive loading completed successfully');
      }
    }
  };

  const [{ data: postsData, fetching: fetchingInitialPosts, error: errorFetchingPosts }, refetchPosts] = useQuery({
    query: getSocialAnalyticsPostsWithInsights,
    variables: {
      clientId: clientId || '',
      startDate: queryVariables.startDate || '',
      endDate: queryVariables.endDate || '',
      postTypes: queryVariables.postTypes || [],
      includeEstimates: queryVariables.includeEstimates || false,
      postDateIntervalEnabled: queryVariables.postDateIntervalEnabled || false,
      networks: queryVariables.networks || [],
      projectIds: queryVariables.projectIds,
      groupIds: queryVariables.groupIds,
      memberIds: queryVariables.memberIds,
      segments: queryVariables.segments,
      mentionSources: queryVariables.mentionSources,
      paging: {
        limit: PAGING_CONFIG.limit,
        offset: 0,
      },
    },
    pause: !clientId || tab !== 'posts',
  });

  // Set total posts immediately when pagination data is available
  useEffect(() => {
    if (postsData?.client?.socialAnalytics?.posts?.pagination) {
      const totalCount = postsData.client.socialAnalytics.posts.pagination.totalCount;
      // Update totalPosts if it's 0 (initial load or reset) or if the count has changed
      if (totalPosts === 0 || totalPosts !== totalCount) {
        setTotalPosts(totalCount);
      }
    }
  }, [postsData, totalPosts]);

  // Calculate actual loaded posts from the data we have
  const loadedPosts = useMemo(() => {
    const firstPageCount = postsData?.client?.socialAnalytics?.posts?.results?.length || 0;
    const additionalPageCount = additionalPosts.length;
    return firstPageCount + additionalPageCount;
  }, [postsData?.client?.socialAnalytics?.posts?.results?.length, additionalPosts.length]);

  // Auto-load all pages when first page is ready
  useEffect(() => {
    // Guard: Don't run if already fetching additional posts
    if (isFetchingRef.current) return;

    // Guard: Only run for posts tab
    if (tab !== 'posts') return;

    // Guard: Don't run if initial query is still fetching
    if (fetchingInitialPosts) return;

    // Guard: Only run when we have initial data
    if (!postsData?.client?.socialAnalytics?.posts?.pagination?.totalCount) return;

    // Guard: Don't run if we already have additional posts (avoid re-fetching)
    if (additionalPosts.length > 0) return;

    const totalCount = postsData.client.socialAnalytics.posts.pagination.totalCount;
    const currentCount = postsData.client.socialAnalytics.posts.results?.length || 0;

    // Guard: Don't run if we already have all posts
    if (currentCount >= totalCount) return;

    // Use cache bypass if in refresh mode, otherwise use cache
    startProgressiveLoadingRef.current?.({ totalCount, currentCount, bypassCache: isRefreshMode });
  }, [postsData, tab, additionalPosts.length, fetchingInitialPosts, isRefreshMode]);

  const handleRefresh = useCallback(() => {
    if (tab === 'posts') {
      resetProgressiveState();
      setIsRefreshMode(true); // Set refresh mode before refetch
      refetchPosts({ requestPolicy: 'network-only' });
    }
  }, [refetchPosts, tab, resetProgressiveState]);

  // Function to update local assignments
  const updateLocalAssignment = useCallback(
    (update: Omit<LocalUpdate, 'timestamp'>) => {
      setLocalUpdates((prev) => {
        // Move the info-level log inside the updater so we can use the fresh `prev` state
        logger.debug('[usePostsData] Updating local assignment:', {
          entityId: update.entityId,
          previousProject: prev[update.entityId]?.projectId,
          newProjectId: update.projectId,
          newProjectName: update.projectName,
          previousGroup: prev[update.entityId]?.groupId,
          newGroupId: update.groupId,
          newGroupName: update.groupName,
        });

        const newUpdate = {
          ...update,
          timestamp: Date.now(),
        };

        // Preserve the debug log as-before
        logger.debug('[usePostsData] Local updates state:', {
          totalUpdates: Object.keys(prev).length,
          updatedEntityId: update.entityId,
          timestamp: new Date(newUpdate.timestamp).toISOString(),
        });

        return {
          ...prev,
          [update.entityId]: newUpdate,
        };
      });
    },
    [], // now stable — no more `localUpdates` in the deps
  );

  const resolveProject = useCallback(
    (
      post: PostInsights,
      localUpdate: LocalUpdate | undefined,
      useLocalUpdate: boolean,
    ): { id: number; name: string } | null => {
      let project: Project | undefined;

      if (useLocalUpdate && localUpdate?.projectId) {
        project = projects.find((p) => p.id.toString() === localUpdate.projectId);
        logger.debug('[usePostsData] Project resolution (local update):', {
          postId: post.entityId,
          localUpdateProjectId: localUpdate.projectId,
          foundProject: project?.name,
          fallbackName: localUpdate.projectName,
        });
        if (!project && localUpdate.projectName) {
          const numericId = Number(localUpdate.projectId);
          project = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: localUpdate.projectName,
          };
        }
      } else if (post.projectId) {
        project = projects.find((p) => p.id.toString() === post.projectId);
        if (!project) {
          const numericId = Number(post.projectId);
          project = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: `Unknown(${post.projectId})`,
          };
        }
      }

      return project ? { id: project.id, name: project.name } : null;
    },
    [projects],
  );

  const resolveGroup = useCallback(
    (
      post: PostInsights,
      localUpdate: LocalUpdate | undefined,
      useLocalUpdate: boolean,
    ): { id: number; name: string } | null => {
      let group: Group | undefined;

      if (useLocalUpdate && localUpdate?.groupId) {
        group = groups.find((g) => g.id.toString() === localUpdate.groupId);
        logger.debug('[usePostsData] Group resolution (local update):', {
          postId: post.entityId,
          localUpdateGroupId: localUpdate.groupId,
          foundGroup: group?.name,
          fallbackName: localUpdate.groupName,
        });
        if (!group && localUpdate.groupName) {
          const numericId = Number(localUpdate.groupId);
          group = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: localUpdate.groupName,
          };
        }
      } else if (post.groupId) {
        group = groups.find((g) => g.id.toString() === post.groupId);
        if (!group) {
          const numericId = Number(post.groupId);
          group = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: `Unknown(${post.groupId})`,
          };
        }
      }

      return group ? { id: group.id, name: group.name } : null;
    },
    [groups],
  );

  // Helper function to enrich a post with project and group data
  const enrichPost = useCallback(
    (post: PostInsights): EnrichedPost => {
      const localUpdate = localUpdates[post.entityId];
      const useLocalUpdate = localUpdate && Date.now() - localUpdate.timestamp < 5 * 60 * 1000;

      return {
        ...post,
        projectId: useLocalUpdate ? localUpdate.projectId : post.projectId,
        groupId: useLocalUpdate ? localUpdate.groupId : post.groupId,
        project: resolveProject(post, localUpdate, useLocalUpdate),
        group: resolveGroup(post, localUpdate, useLocalUpdate),
      };
    },
    [localUpdates, resolveProject, resolveGroup],
  );

  // Memoize enriched first page separately to avoid re-enriching
  const enrichedFirstPage = useMemo<EnrichedPost[]>(() => {
    if (!postsData?.client?.socialAnalytics?.posts?.results) {
      return [];
    }

    return postsData.client.socialAnalytics.posts.results.map(enrichPost);
  }, [postsData?.client?.socialAnalytics?.posts?.results, enrichPost]);

  // Memoize enriched additional pages separately
  const enrichedAdditionalPages = useMemo<EnrichedPost[]>(() => {
    return additionalPosts.map(enrichPost);
  }, [additionalPosts, enrichPost]);

  // Combine enriched results
  const filteredData = useMemo<EnrichedPostsData | undefined>(() => {
    // Don't return anything if we don't have the basic data structure yet
    if (!postsData?.client?.socialAnalytics?.posts) {
      return undefined;
    }

    // Combine and deduplicate posts based on entityId
    const allEnrichedPosts = [...enrichedFirstPage, ...enrichedAdditionalPages];
    const uniquePosts = allEnrichedPosts.filter(
      (post, index, arr) => arr.findIndex((p) => p.entityId === post.entityId) === index,
    );

    return {
      ...postsData,
      client: {
        ...postsData.client,
        socialAnalytics: {
          ...postsData.client.socialAnalytics,
          posts: {
            ...postsData.client.socialAnalytics.posts,
            results: uniquePosts,
            pagination: {
              ...postsData.client.socialAnalytics.posts.pagination,
              totalCount: totalPosts || postsData.client.socialAnalytics.posts.pagination.totalCount,
            },
          },
        },
      },
    };
  }, [postsData, enrichedFirstPage, enrichedAdditionalPages, totalPosts]);

  const fetchProgress = totalPosts > 0 ? Math.round((loadedPosts / totalPosts) * 100) : 0;
  const fetchingPosts = fetchingInitialPosts || fetchingAdditionalPosts;

  return {
    data: filteredData,
    error: errorFetchingPosts,
    fetchingPosts,
    fetchingInitialPosts,
    fetchingAdditionalPosts,
    fetchProgress,
    totalPosts,
    loadedPosts,
    handleRefresh,
    updateLocalAssignment,
  };
};
